/*
  PathwayAllowListWidget — Phase 7 pathway expansion admin surface.
  Clinical-lead picks a pathway and toggles per-field overrides:

    · force_A → opt an ineligible narrative field into extraction.
    · block   → hide a natural Class A field from extraction.

  Reads:  GET  /governance/pathway-allow-list          (module list)
          GET  /governance/pathway-allow-list/{module} (fields detail)
  Writes: PUT  /governance/pathway-allow-list/{module}/{field}  {action}
          DELETE ...                                    (clear)

  Kept intentionally compact — this is a governance surface, so the
  primary UX is scannability + safe defaults, not slick styling.
  Overrides drive the extraction pipeline immediately.
*/
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Layers, Check, Ban, RefreshCcw } from "lucide-react";


export default function PathwayAllowListWidget() {
  const [summary, setSummary] = useState(null);
  const [selectedModule, setSelectedModule] = useState("");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [search, setSearch] = useState("");

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/governance/pathway-allow-list");
      setSummary(r.data);
      if (!selectedModule && r.data.modules?.length) {
        setSelectedModule(r.data.modules[0].module_key);
      }
    } catch (e) {
      toast.error("Failed to load pathway summary", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setLoading(false); }
  }, [selectedModule]);

  const loadDetail = useCallback(async (moduleKey) => {
    if (!moduleKey) { setDetail(null); return; }
    setLoading(true);
    try {
      const r = await api.get(`/governance/pathway-allow-list/${moduleKey}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(`Failed to load ${moduleKey}`, {
        description: e?.response?.data?.detail || e.message,
      });
      setDetail(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { if (selectedModule) loadDetail(selectedModule); }, [selectedModule, loadDetail]);

  async function toggle(field, action) {
    const rowKey = `${detail.module_key}:${field.field_key}`;
    setSavingKey(rowKey);
    try {
      if (field.override === action) {
        await api.delete(`/governance/pathway-allow-list/${detail.module_key}/${field.field_key}`);
      } else {
        await api.put(`/governance/pathway-allow-list/${detail.module_key}/${field.field_key}`, {
          action,
        });
      }
      await Promise.all([loadDetail(detail.module_key), loadSummary()]);
    } catch (e) {
      toast.error("Override change failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setSavingKey(null); }
  }

  const filteredFields = useMemo(() => {
    if (!detail) return [];
    const q = search.trim().toLowerCase();
    if (!q) return detail.fields;
    return detail.fields.filter((f) =>
      (f.field_key || "").toLowerCase().includes(q) ||
      (f.field_label || "").toLowerCase().includes(q) ||
      (f.section_key || "").toLowerCase().includes(q),
    );
  }, [detail, search]);

  return (
    <Card className="p-4 flex flex-col" data-testid="pathway-allow-list-widget">
      <div className="flex items-center gap-2 border rounded-md px-3 py-2 mb-3 border-indigo-200 bg-indigo-50 text-indigo-900">
        <Layers className="h-4 w-4 shrink-0" />
        <div className="font-semibold text-sm">Pathway allow-list — clinical-lead overrides</div>
        <Button
          size="sm" variant="ghost"
          className="ml-auto h-6 px-2 text-indigo-900 hover:bg-indigo-100"
          onClick={() => { loadSummary(); if (selectedModule) loadDetail(selectedModule); }}
          data-testid="pathway-refresh-btn"
        >
          <RefreshCcw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* Module summary + picker */}
      <div className="flex flex-wrap gap-1 mb-3">
        {(summary?.modules || []).map((m) => {
          const active = m.module_key === selectedModule;
          const badge = m.force_A_overrides + m.block_overrides;
          return (
            <button
              key={m.module_key}
              onClick={() => setSelectedModule(m.module_key)}
              className={`text-xs px-2 py-1 rounded border inline-flex items-center gap-1 ${
                active
                  ? "border-indigo-600 bg-indigo-600 text-white"
                  : "border-indigo-300 bg-white text-indigo-900 hover:bg-indigo-100"
              }`}
              data-testid={`pathway-pick-${m.module_key}`}
            >
              {m.module_label || m.module_key}
              <span className={`text-2xs font-mono-num ${active ? "text-indigo-100" : "text-indigo-500"}`}>
                {m.eligible_fields}
              </span>
              {badge > 0 && (
                <span className={`text-2xs rounded-full px-1 ${
                  active ? "bg-white text-indigo-800" : "bg-indigo-100 text-indigo-800"
                }`}>
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Field table */}
      {detail ? (
        <>
          <div className="flex items-center gap-2 mb-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter fields by key, label, or section…"
              className="flex-1 text-xs border border-zinc-300 rounded px-2 py-1"
              data-testid="pathway-search"
            />
            <span className="text-2xs text-zinc-500">
              {filteredFields.length} / {detail.total_fields} · {detail.total_eligible} eligible
            </span>
          </div>
          <div className="border border-zinc-200 rounded overflow-hidden">
            <div className="max-h-[420px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-zinc-50 text-zinc-600 sticky top-0">
                  <tr className="text-left">
                    <th className="px-2 py-1 font-semibold">Field</th>
                    <th className="px-2 py-1 font-semibold">Natural</th>
                    <th className="px-2 py-1 font-semibold">Effective</th>
                    <th className="px-2 py-1 font-semibold text-right">Override</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFields.map((f) => {
                    const rowKey = `${detail.module_key}:${f.field_key}`;
                    const saving = savingKey === rowKey;
                    return (
                      <tr key={f.field_key}
                          className={`border-t border-zinc-100 hover:bg-zinc-50 ${f.safety_critical ? "bg-rose-50/40" : ""}`}
                          data-testid={`pathway-row-${f.field_key}`}>
                        <td className="px-2 py-1.5">
                          <div className="font-medium text-zinc-900">{f.field_label || f.field_key}</div>
                          <div className="text-2xs text-zinc-500 font-mono-num">
                            {f.section_key} · {f.field_key} · {f.field_type}
                            {f.safety_critical && (
                              <span className="ml-1 text-rose-700 font-semibold">safety</span>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-1.5">
                          <ClassChip cls={f.natural_class} eligible={f.natural_eligible} />
                        </td>
                        <td className="px-2 py-1.5">
                          <ClassChip cls={f.effective_class} eligible={f.effective_eligible} />
                        </td>
                        <td className="px-2 py-1.5 text-right whitespace-nowrap">
                          <OverrideBtn
                            active={f.override === "force_A"}
                            disabled={saving || f.safety_critical}
                            title={f.safety_critical
                              ? "Safety-critical fields cannot be forced to Class A"
                              : "Force this field into Class A extraction"}
                            onClick={() => toggle(f, "force_A")}
                            tone="emerald"
                            icon={Check}
                            label="force A"
                            testId={`pathway-force-${f.field_key}`}
                          />
                          <OverrideBtn
                            active={f.override === "block"}
                            disabled={saving}
                            title="Block this field from extraction"
                            onClick={() => toggle(f, "block")}
                            tone="rose"
                            icon={Ban}
                            label="block"
                            testId={`pathway-block-${f.field_key}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="text-sm text-zinc-500 italic p-4 text-center">
          {loading ? "Loading…" : "Select a pathway to review fields."}
        </div>
      )}
    </Card>
  );
}


function ClassChip({ cls, eligible }) {
  const tone = eligible
    ? "border-emerald-300 bg-emerald-100 text-emerald-900"
    : cls === "A" ? "border-zinc-300 bg-zinc-100 text-zinc-700"
    : "border-zinc-300 bg-white text-zinc-600";
  return (
    <span className={`text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border ${tone}`}>
      {cls}{eligible && " ✓"}
    </span>
  );
}


function OverrideBtn({ active, onClick, disabled, tone, icon: Icon, label, testId, title }) {
  const base = active
    ? tone === "emerald"
      ? "border-emerald-600 bg-emerald-600 text-white"
      : "border-rose-600 bg-rose-600 text-white"
    : tone === "emerald"
      ? "border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-50"
      : "border-rose-300 bg-white text-rose-800 hover:bg-rose-50";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`inline-flex items-center gap-0.5 text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border ml-1 disabled:opacity-40 disabled:cursor-not-allowed ${base}`}
      data-testid={testId}
    >
      <Icon className="h-2.5 w-2.5" />
      {label}
    </button>
  );
}
